import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button, MenuItem, Autocomplete,
  Typography, Box, Divider, useMediaQuery,
} from '@mui/material';
import api from '../../adapters/api';
import Swal from 'sweetalert2';

const defaultForm = {
  name_1: '',
  ar_status: 0,
  province: '',
  amper: '',
  tambon: '',
  zip_code: '',
  address: '',
  telephone: '',
  email: '',
  ar_type: '',
  set_tax_type: 0,
  card_id: '',
  tax_id: '',
  branch_type: 0,
  branch_code: '',
  price_level: 0,
  sale_code: '',
  sale_type: 1,
};

const SectionLabel = ({ children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
    >
      {children}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Box>
);

export default function NewCustomerDialog({ open, onClose, onSuccess }) {
  // มือถือ: เต็มจอ + ช่องกรอกเรียงแถวละช่อง (สองช่องต่อแถวแคบจนอ่านไม่ออก)
  const isPhone = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [form, setForm] = useState(defaultForm);
  const [provinceList, setProvinceList] = useState([]);
  const [amperList, setAmperList] = useState([]);
  const [tambonList, setTambonList] = useState([]);
  const [saleList, setSaleList] = useState([]);
  const [arTypes, setArTypes] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const name = form.name_1.trim();
    const isJuristic = /^(บริษัท|หจก\.|ห้างหุ้นส่วน|มูลนิธิ)/.test(name);
    const newStatus = isJuristic ? 1 : 0;
    const newTaxType = isJuristic ? 1 : form.set_tax_type;
    const newBranchCode = isJuristic ? '00000' : '';
    if (form.ar_status !== newStatus || form.set_tax_type !== newTaxType || form.branch_code !== newBranchCode) {
      setForm(prev => ({ ...prev, ar_status: newStatus, set_tax_type: newTaxType, branch_code: newBranchCode }));
    }
  }, [form.name_1]);

  useEffect(() => {
    async function fetchData() {
      const [prov, amper, tambon, sales, types] = await Promise.all([
        api.post('/api/getProvinceList'),
        api.post('/api/getAmperList'),
        api.post('/api/getTambonList'),
        api.post('/api/getSalespersons'),
        api.post('/api/customerType'),
      ]);
      setProvinceList(prov.data);
      setAmperList(amper.data);
      setTambonList(tambon.data);
      setSaleList(sales.data);
      setArTypes(types.data);
    }
    if (open) fetchData();
  }, [open]);

  const selectedProvince = useMemo(() => provinceList.find(p => p.code === form.province) || null, [form.province, provinceList]);
  const selectedAmper = useMemo(() => amperList.find(a => a.code === form.amper) || null, [form.amper, amperList]);
  const selectedTambon = useMemo(() => tambonList.find(t => t.code === form.tambon) || null, [form.tambon, tambonList]);
  const isCreditGroup = useMemo(() => {
    const user = saleList.find(s => s.code === form.sale_code);
    return user?.groups?.some(g => g.group_code === 'แผนกขายเครดิต');
  }, [form.sale_code, saleList]);

  useEffect(() => {
    if (!isCreditGroup) {
      setForm(prev => ({ ...prev, sale_type: 1, price_level: 0 }));
    }
  }, [isCreditGroup]);

  useEffect(() => {
    if (form.branch_type === 0) {
      setForm(prev => ({ ...prev, branch_code: '00000' }));
    } else if (form.branch_code === '00000') {
      setForm(prev => ({ ...prev, branch_code: '' }));
    }
  }, [form.branch_type]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      const updated = { ...prev };
      if (value && prev[field]) delete updated[field];
      return updated;
    });
  };

  const buildFullAddress = (manual, tambon, amper, province, zip) => {
    const location = tambon && amper && province
      ? `ต.${tambon.name_1} อ.${amper.name_1} จ.${province.name_1} ${zip}`
      : '';
    return manual.trim() + (location ? ` ${location}` : '');
  };

  const stripLocationSuffix = (text) =>
    text
      .replace(/\s*ต\.[^\s]+/, '')
      .replace(/\s*อ\.[^\s]+/, '')
      .replace(/\s*จ\.[^\s]+/, '')
      .replace(/\s*\d{5}$/, '')
      .trim();

  const handleAutoCompleteChange = (field) => (_, value) => {
    const code = value?.code || '';
    setForm(prev => {
      const cleanedAddress = stripLocationSuffix(prev.address);
      const updated = { ...prev, [field]: code };
      if (field === 'province') {
        updated.amper = '';
        updated.tambon = '';
        updated.zip_code = '';
        updated.address = cleanedAddress;
      } else if (field === 'amper') {
        updated.tambon = '';
        updated.zip_code = '';
        updated.address = cleanedAddress;
      } else if (field === 'tambon') {
        const tambon = tambonList.find(t => t.code === code);
        const amper = amperList.find(a => a.code === tambon?.amper);
        const province = provinceList.find(p => p.code === tambon?.province);
        updated.zip_code = tambon?.zip_code || '';
        updated.address = buildFullAddress(prev.address?.split('ต.')[0] || '', tambon, amper, province, updated.zip_code);
      }
      return updated;
    });
    setErrors(prev => {
      const updated = { ...prev };
      if (code && prev[field]) delete updated[field];
      return updated;
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name_1) newErrors.name_1 = 'กรุณาระบุชื่อ';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'อีเมลไม่ถูกต้อง';
    const isCompany = form.ar_status === 1;
    const isGov = form.ar_type === 'หน่วยงานภาครัฐ';
    const requireFullAddress = form.set_tax_type === 1;
    if (requireFullAddress) {
      if (!form.province) newErrors.province = 'กรุณาเลือกจังหวัด';
      if (!form.amper) newErrors.amper = 'กรุณาเลือกอำเภอ';
      if (!form.tambon) newErrors.tambon = 'กรุณาเลือกตำบล';
      if (!form.address.trim()) newErrors.address = 'กรุณาระบุที่อยู่';
      if (!form.ar_type) newErrors.ar_type = 'กรุณาเลือกประเภทลูกค้า';
    }
    if (!isCompany && requireFullAddress) {
      if (!/^\d{13}$/.test(form.card_id)) newErrors.card_id = 'เลขบัตรประชาชนไม่ถูกต้อง';
    }
    if (isCompany && !isGov && requireFullAddress) {
      if (!/^\d{13}$/.test(form.tax_id)) newErrors.tax_id = 'เลขผู้เสียภาษีไม่ถูกต้อง';
      if (form.branch_type === 1 && !/^\d{5}$/.test(form.branch_code)) {
        newErrors.branch_code = 'เลขที่สาขาไม่ถูกต้อง';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveCustomer = async () => {
    if (!validate()) return;
    const selectedSale = saleList.find(s => s.code === form.sale_code);
    const groupNames = selectedSale?.groups?.map(g => g.group_name) || [];
    const response = await api.post('/api/checkSimilarCustomerName', { name: form.name_1 });
    if (response.data.similar.length > 0) {
      const similarNames = response.data.similar.map(c => `• ${c.name_1}`).join('<br/>');
      const confirm = await Swal.fire({
        title: 'ชื่อซ้ำกับลูกค้าเดิม!',
        html: `พบชื่อลูกค้าที่ใกล้เคียง:<br/><br/>${similarNames}<br/><br/>คุณต้องการบันทึกต่อหรือไม่?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1976d2',
        cancelButtonColor: '#757575',
        confirmButtonText: 'บันทึกต่อ',
        cancelButtonText: 'ยกเลิก',
      });
      if (!confirm.isConfirmed) return;
    }
    try {
      const res = await api.post('/api/createCustomer', {
        ...form,
        sale_name: selectedSale?.name_2 || '',
        sale_groups: groupNames,
      });
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'เพิ่มลูกค้าใหม่สำเร็จ',
          html: `<p><strong>รหัสลูกค้า:</strong> ${res.data.code}</p><p><strong>ชื่อลูกค้า:</strong> ${form.name_1}</p>`,
          confirmButtonColor: '#2e7d32',
          confirmButtonText: 'ตกลง',
        }).then(() => {
          onSuccess({
            code: res.data.code,
            name_1: form.name_1,
            email: form.email || '',
            price_level: form.price_level,
            sale_type: form.sale_type === 2 ? 'เครดิต' : 'เงินสด',
            set_tax_type: form.set_tax_type === 1 ? 'vat' : 'novat',
            credit_day: 0,
          });
          setForm(defaultForm);
          setErrors({});
          onClose();
        });
      } else {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: res.data.error || res.data.message || 'ไม่สามารถเพิ่มลูกค้าได้' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || err.message || 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์' });
    }
  };

  const isCompany = form.ar_status === 1;
  const requireTaxInfo = form.set_tax_type === 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isPhone}>
      <DialogTitle sx={{ pb: 1 }}>เพิ่มลูกค้าใหม่</DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* ข้อมูลทั่วไป */}
          <Box>
            <SectionLabel>ข้อมูลทั่วไป</SectionLabel>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  label="ชื่อลูกค้า"
                  size="small"
                  fullWidth
                  required
                  value={form.name_1}
                  onChange={handleChange('name_1')}
                  error={!!errors.name_1}
                  helperText={errors.name_1}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="เบอร์โทร"
                  inputProps={{ inputMode: 'tel' }}
                  size="small"
                  fullWidth
                  value={form.telephone}
                  onChange={handleChange('telephone')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  size="small"
                  fullWidth
                  value={form.email}
                  onChange={handleChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
            </Grid>
          </Box>

          {/* การขาย */}
          <Box>
            <SectionLabel>การขาย</SectionLabel>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={saleList.filter(Boolean)}
                  getOptionLabel={o => o?.name_1 ?? ''}
                  isOptionEqualToValue={(o, v) => o?.code === v?.code}
                  value={saleList.find(s => s.code === form.sale_code) || null}
                  onChange={(_, v) => setForm(prev => ({ ...prev, sale_code: v?.code || '' }))}
                  renderInput={p => <TextField {...p} label="พนักงานขาย" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="ประเภทภาษี"
                  value={form.set_tax_type}
                  onChange={handleChange('set_tax_type')}
                >
                  <MenuItem value={1}>VAT</MenuItem>
                  <MenuItem value={0}>No VAT</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="รูปแบบการขาย"
                  value={form.sale_type}
                  onChange={handleChange('sale_type')}
                  disabled={!isCreditGroup}
                >
                  <MenuItem value={1}>เงินสด</MenuItem>
                  <MenuItem value={2}>เครดิต</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="ระดับราคาขาย"
                  value={form.price_level}
                  onChange={handleChange('price_level')}
                  disabled={!isCreditGroup}
                >
                  <MenuItem value={0}>สด</MenuItem>
                  <MenuItem value={1}>7%</MenuItem>
                  <MenuItem value={2}>10%</MenuItem>
                  <MenuItem value={3}>12%</MenuItem>
                  <MenuItem value={4}>15%</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {/* ที่อยู่ */}
          <Box>
            <SectionLabel>ที่อยู่</SectionLabel>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  value={selectedProvince}
                  options={provinceList.filter(Boolean)}
                  getOptionLabel={o => o?.name_1 ?? ''}
                  isOptionEqualToValue={(o, v) => o?.code === v?.code}
                  onChange={handleAutoCompleteChange('province')}
                  renderInput={p => (
                    <TextField {...p} label="จังหวัด" error={!!errors.province} helperText={errors.province} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  value={selectedAmper}
                  options={amperList.filter(a => a && a.province === form.province)}
                  getOptionLabel={o => o?.name_1 ?? ''}
                  isOptionEqualToValue={(o, v) => o?.code === v?.code}
                  onChange={handleAutoCompleteChange('amper')}
                  renderInput={p => (
                    <TextField {...p} label="อำเภอ" error={!!errors.amper} helperText={errors.amper} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  value={selectedTambon}
                  options={tambonList.filter(t => t && t.amper === form.amper)}
                  getOptionLabel={o => o?.name_1 ?? ''}
                  isOptionEqualToValue={(o, v) => o?.code === v?.code}
                  onChange={handleAutoCompleteChange('tambon')}
                  renderInput={p => (
                    <TextField {...p} label="ตำบล" error={!!errors.tambon} helperText={errors.tambon} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="รหัสไปรษณีย์"
                  inputProps={{ inputMode: 'numeric' }}
                  size="small"
                  fullWidth
                  value={form.zip_code}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="ที่อยู่"
                  size="small"
                  fullWidth
                  value={form.address}
                  onChange={handleChange('address')}
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </Grid>
            </Grid>
          </Box>

          {/* ประเภทลูกค้า */}
          <Box>
            <SectionLabel>ประเภทลูกค้า</SectionLabel>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="สถานะนิติบุคคล"
                  size="small"
                  fullWidth
                  value={isCompany ? 'นิติบุคคล' : 'บุคคล'}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={arTypes.filter(Boolean)}
                  getOptionLabel={o => o?.name_1 ?? ''}
                  isOptionEqualToValue={(o, v) => o?.code === v?.code}
                  value={arTypes.find(t => t.code === form.ar_type) || null}
                  onChange={(_, v) => setForm(prev => ({ ...prev, ar_type: v?.code || '' }))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="ประเภทลูกค้า"
                      error={!!errors.ar_type}
                      helperText={errors.ar_type}
                    />
                  )}
                />
              </Grid>

              {/* บุคคล + VAT → บัตรประชาชน */}
              {!isCompany && requireTaxInfo && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="เลขบัตรประชาชน"
                    size="small"
                    fullWidth
                    value={form.card_id}
                    onChange={handleChange('card_id')}
                    error={!!errors.card_id}
                    helperText={errors.card_id}
                    inputProps={{ maxLength: 13 }}
                  />
                </Grid>
              )}

              {/* นิติบุคคล (ไม่ใช่รัฐ) + VAT → ผู้เสียภาษี + สาขา */}
              {isCompany && form.ar_type !== '00' && requireTaxInfo && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="เลขผู้เสียภาษี"
                      size="small"
                      fullWidth
                      value={form.tax_id}
                      onChange={handleChange('tax_id')}
                      error={!!errors.tax_id}
                      helperText={errors.tax_id}
                      inputProps={{ maxLength: 13 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="ประเภทสาขา"
                      value={form.branch_type}
                      onChange={handleChange('branch_type')}
                    >
                      <MenuItem value={0}>สำนักงานใหญ่</MenuItem>
                      <MenuItem value={1}>สาขา</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="เลขที่สาขา"
                      size="small"
                      fullWidth
                      value={form.branch_code}
                      onChange={handleChange('branch_code')}
                      disabled={form.branch_type === 0}
                      error={!!errors.branch_code}
                      helperText={errors.branch_code}
                      inputProps={{ maxLength: 5 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">ยกเลิก</Button>
        <Button onClick={handleSaveCustomer} variant="contained">บันทึก</Button>
      </DialogActions>
    </Dialog>
  );
}
